import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Connector, AgentConnectorSummary, ConnectorAgentRef, McpStatusResponse } from "@/types";

// #231 (UC-4): Connector-Katalog (Liste + Detail, je mit connection-Status).
//
// #326: `enabled` für Aufrufer, die den Katalog nur als Auswahlhilfe brauchen — wer den
// Vorgang `connectors.view` nicht hat, soll nicht auf Verdacht in ein 403 laufen.
export function useConnectors(opts: { enabled?: boolean } = {}): UseQueryResult<Connector[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("connectors"),
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<Connector[]>("/connectors");
    },
    enabled: !!session && (opts.enabled ?? true),
  });
}

export function useConnector(id: string | undefined): UseQueryResult<Connector> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("connectors", id) : ["resources", "connectors", "_invalid"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<Connector>(`/connectors/${id}`);
    },
    enabled: !!session && !!id,
  });
}

// #231 (UC-4): Reverse-Lookup — welche Agents haben diesen Connector aktiviert (Detail).
export function useConnectorAgents(id: string | undefined): UseQueryResult<ConnectorAgentRef[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.subResource("connectors", id, "agents") : ["resources", "connectors", "_invalid", "agents"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ConnectorAgentRef[]>(`/connectors/${id}/agents`);
    },
    enabled: !!session && !!id,
  });
}

// #231 (UC-3/4): Katalog-Connectors + enabled-Flag für EINEN Agent (Per-Agent-Section).
export function useAgentConnectors(agentId: string | undefined): UseQueryResult<AgentConnectorSummary[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: agentId
      ? queryKeys.subResource("agents", agentId, "connectors")
      : ["resources", "agents", "_invalid", "connectors"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<AgentConnectorSummary[]>(`/agents/${agentId}/connectors`);
    },
    enabled: !!session && !!agentId,
  });
}

// #237 (UC-status): Live-MCP-Server-Status des Brains. MANUELL ausgelöst (enabled:false) —
// die Abfrage startet im Brain eine kurze Query + connectet die MCP-Server (Kosten/Latenz),
// daher kein Auto-Fetch beim Seitenaufruf; der User klickt „abrufen" (refetch).
export function useAgentMcpStatus(agentId: string | undefined): UseQueryResult<McpStatusResponse> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: agentId
      ? queryKeys.subResource("agents", agentId, "mcp-status")
      : ["resources", "agents", "_invalid", "mcp-status"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<McpStatusResponse>(`/agents/${agentId}/mcp-status`);
    },
    enabled: false, // manuell via refetch
    staleTime: 0,
  });
}
